import { create } from 'zustand';
import * as favApi from '@/services/favoritesApi';
import { useAuth } from './useAuth';
import { isSafeReturnPath } from '@/utils/panelRedirect';

export const useFavorites = create((set, get) => ({
  defaultCollectionId: null,
  collections: [],
  mlsToItem: {},
  hydrated: false,
  loading: false,

  hydrate: async () => {
    const isAuthenticated = useAuth.getState().isAuthenticated;
    if (!isAuthenticated) {
      set({ hydrated: true, collections: [], mlsToItem: {}, defaultCollectionId: null });
      return;
    }

    try {
      const cols = await favApi.listCollections();
      let defaultCol = cols.find((c) => c.is_default);

      if (!defaultCol) {
        defaultCol = await favApi.createCollection('My Favorites', null, null, 0);
      }

      const colWithItems = await favApi.getCollection(defaultCol.id);
      const mlsMap = {};
      (colWithItems.items || []).forEach((item) => {
        mlsMap[item.mls_number] = { collectionId: defaultCol.id, ...item };
      });

      set({
        collections: cols,
        defaultCollectionId: defaultCol.id,
        mlsToItem: mlsMap,
        hydrated: true,
      });

    } catch (err) {
      console.error('Favorites hydrate error', err);
      set({ hydrated: true });
    }
  },

  isFavorite: (mlsNumber) => !!get().mlsToItem[mlsNumber],

  toggle: async (mlsNumber) => {
    const isAuthenticated = useAuth.getState().isAuthenticated;
    if (!isAuthenticated) {
      const { toast } = await import('sonner');
      toast('Sign in to save homes', {
        description: 'Create a free account to save and track your favorite listings.',
        action: {
          label: 'Sign in',
          onClick: () => {
            const returnTo = `${window.location.pathname}${window.location.search}`;
            const safe = isSafeReturnPath(returnTo) ? returnTo : '/';
            window.location.href = `/login?redirect=${encodeURIComponent(safe)}`;
          },
        },
      });
      return false;
    }

    const { defaultCollectionId, mlsToItem } = get();
    if (!defaultCollectionId) return false;

    const isFav = !!mlsToItem[mlsNumber];

    set((state) => {
      const newMap = { ...state.mlsToItem };
      if (isFav) {
        delete newMap[mlsNumber];
      } else {
        newMap[mlsNumber] = { collectionId: defaultCollectionId, mls_number: mlsNumber, added_at: new Date().toISOString() };
      }
      return { mlsToItem: newMap };
    });

    try {
      if (isFav) {
        await favApi.removeItem(defaultCollectionId, mlsNumber);
      } else {
        await favApi.addItem(defaultCollectionId, mlsNumber);
      }
      return true;
    } catch (err) {
      set((state) => {
        const rollback = { ...state.mlsToItem };
        if (isFav) {
          rollback[mlsNumber] = { collectionId: defaultCollectionId, mls_number: mlsNumber };
        } else {
          delete rollback[mlsNumber];
        }
        return { mlsToItem: rollback };
      });
      const { toast } = await import('sonner');
      toast.error(isFav ? 'Could not remove favorite. Please try again.' : 'Could not save favorite. Please try again.');
      return false;
    }
  },

  getIds: () => Object.keys(get().mlsToItem),
}));
